import type { Coords } from "@hoa/protocol";

const R = 6_371_008.8; // rayon moyen de la Terre, en mètres
const rad = (d: number) => (d * Math.PI) / 180;

/** Distance orthodromique en mètres (formule de haversine). */
export function distanceM(a: Coords, b: Coords): number {
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

export type Band = "far" | "approach" | "near" | "here";

/**
 * Paliers sonores de l'approche (D-07). Le seuil « here » est le rayon de
 * réunion configuré dans l'expérience, pas une constante.
 */
export function band(distance: number, gatherRadiusM: number): Band {
  if (distance <= gatherRadiusM) return "here";
  if (distance <= 100) return "near";
  if (distance <= 300) return "approach";
  return "far";
}

/**
 * Déplace un point de `meters` dans la direction `bearing` (degrés).
 * Utilisé par le simulateur de participants pour faire marcher des humains
 * virtuels vers leur point de rendez-vous.
 */
export function move(from: Coords, bearing: number, meters: number): Coords {
  const br = rad(bearing);
  const lat1 = rad(from.lat);
  const lon1 = rad(from.lon);
  const d = meters / R;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(br));
  const lon2 =
    lon1 +
    Math.atan2(Math.sin(br) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
  return { lat: (lat2 * 180) / Math.PI, lon: (((lon2 * 180) / Math.PI + 540) % 360) - 180 };
}

/** Cap en degrés de `from` vers `to`. */
export function bearing(from: Coords, to: Coords): number {
  const y = Math.sin(rad(to.lon - from.lon)) * Math.cos(rad(to.lat));
  const x =
    Math.cos(rad(from.lat)) * Math.sin(rad(to.lat)) -
    Math.sin(rad(from.lat)) * Math.cos(rad(to.lat)) * Math.cos(rad(to.lon - from.lon));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}
